import * as Toggle from '@radix-ui/react-toggle-group'
import { useKestrel } from 'features/kestrelViewer'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'
import { KVSurfaceArea } from 'types'

import { DeleteSurfaceAreaButton } from './DeleteSurfaceAreaButton'
import { ToggleSurfaceAreaVisibilityButton } from './ToggleSurfaceAreaVisibilityButton'

const StyledSurfaceAreaEntry = styled(Toggle.Item)<{ $selected: boolean }>`
  all: unset;
  cursor: ${({ $selected }) => ($selected ? 'auto' : 'pointer')} !important;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : ''} !important;
  position: relative;
  padding: 0.25rem;

  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

const PrimaryLineContainer = styled.div<{ $visible: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
  color: ${({ $visible }) => ($visible ? 'white' : 'gray')};
  overflow: hidden;
`

const LeftSideInfo = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  max-width: calc(100% - 80px);
  overflow: hidden;
`

const SurfaceAreaLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`

const ActionsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  position: absolute;
  right: 0;
`

export const SurfaceAreaEntry: FC<{
  surfaceArea: KVSurfaceArea
  selectedMarkupId: string | undefined
}> = ({ surfaceArea, selectedMarkupId }) => {
  const isSelected = surfaceArea.id === selectedMarkupId
  const [isHovered, setIsHovered] = useState(false)
  const { setPopupMenuStartPosition, setSelectedMarkup, sendKVCommand } =
    useKestrel()

  const handleSurfaceAreaClick = (e: React.MouseEvent) => {
    if (setPopupMenuStartPosition) {
      setPopupMenuStartPosition(new Vector2(10, e.clientY - 40))
    }
    const newSelectedMarkup =
      selectedMarkupId === surfaceArea.id ? undefined : surfaceArea
    setSelectedMarkup(newSelectedMarkup)

    // Send command to Kestrel to select/deselect the surface area
    if (sendKVCommand) {
      sendKVCommand({
        command: 'highlightSurfaceArea', // the highlightSurfaceArea command handles un-highlighting others
        surfaceAreaId: newSelectedMarkup?.surfaceAreaId || '',
      })
    }
  }

  return (
    <StyledSurfaceAreaEntry
      value={surfaceArea.id}
      $selected={isSelected}
      onClick={handleSurfaceAreaClick}
      onMouseEnter={() => {
        setIsHovered(true)
        // Only highlight on hover if no surface area is currently selected
        if (sendKVCommand && !selectedMarkupId) {
          sendKVCommand({
            command: 'highlightSurfaceArea',
            surfaceAreaId: surfaceArea.surfaceAreaId,
          })
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false)
        // Only unhighlight all if no surface area is currently selected
        if (sendKVCommand && !selectedMarkupId) {
          sendKVCommand({
            command: 'unhighlightAllSurfaceAreas',
          })
        }
      }}>
      <PrimaryLineContainer $visible={surfaceArea.surfaceAreaVisible}>
        <LeftSideInfo>
          <SurfaceAreaLabel>{surfaceArea.surfaceAreaLabel}</SurfaceAreaLabel>
        </LeftSideInfo>
        {isHovered && (
          <ActionsContainer>
            <ToggleSurfaceAreaVisibilityButton surfaceArea={surfaceArea} />
            <DeleteSurfaceAreaButton surfaceAreaId={surfaceArea.id} />
          </ActionsContainer>
        )}
      </PrimaryLineContainer>
    </StyledSurfaceAreaEntry>
  )
}
